/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
/**
 * CombinedSignpostChart — overlays all of a risk's quantitative signposts on one chart (item 94).
 * Different signposts use different units, so each series is min–max normalised to 0–100 to make
 * them comparable. A tick-box per signpost lets the user include/exclude it from the combined view.
 */
import { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Checkbox, CircularProgress, FormControlLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { protectedResources } from "authConfig";

const API_BASE = protectedResources.apiTodoList.endpoint;
const COLORS = [
  "#52009b",
  "#e91e63",
  "#2196f3",
  "#ff9800",
  "#4caf50",
  "#00bcd4",
  "#9c27b0",
  "#f44336",
  "#795548",
  "#607d8b",
];

// ID200 — index each series to 100 at its first (earliest) value instead of min–max 0–100. Min–max
// stretched every series to fill 0–100, which AMPLIFIED near-flat data so the combined chart no longer
// matched the (raw) individual charts. Indexing to 100 preserves each series' real shape/flatness while
// still letting unlike-unit series overlay comparably (all start at 100 = their baseline).
function normalise(pts) {
  const parsed = (pts || [])
    .map((p) => {
      const v = typeof p.value === "number" ? p.value : parseFloat(p.value);
      const d = p.date ? new Date(p.date) : null;
      const valid = d && !Number.isNaN(d.getTime());
      return { v: Number.isNaN(v) ? null : v, d, valid };
    })
    .filter((x) => x.valid)
    // key/ts come from the real timestamp so points group + sort chronologically.
    .sort((a, b) => a.d.getTime() - b.d.getTime());
  if (!parsed.length) return [];
  const base = parsed.find((x) => x.v != null && x.v !== 0)?.v; // first non-zero value = baseline
  return parsed.map(({ v, d }) => ({
    key: d.toISOString().slice(0, 10),
    ts: d.getTime(),
    label: d.toLocaleDateString(),
    nv: v != null && base ? Math.round((v / base) * 1000) / 10 : null,
  }));
}

export default function CombinedSignpostChart({ signposts = [], execute }) {
  const quant = useMemo(
    () => (signposts || []).filter((s) => (s.subType || "quantitative") !== "qualitative"),
    [signposts]
  );

  const [seriesById, setSeriesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [included, setIncluded] = useState(null); // Set of ids; null = init from quant

  const ids = quant.map((s) => String(s._id || s.id)).join(",");

  useEffect(() => {
    if (!execute || quant.length === 0) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      quant.map((s) => {
        const id = String(s._id || s.id);
        return execute("GET", `${API_BASE}/emergingRisk/signposts/${id}/datapoints`)
          .then((r) => ({ id, pts: Array.isArray(r?.data) ? r.data : [] }))
          .catch(() => ({ id, pts: [] }));
      })
    ).then((results) => {
      if (cancelled) return;
      const m = {};
      results.forEach(({ id, pts }) => {
        m[id] = pts;
      });
      setSeriesById(m);
      setIncluded(new Set(quant.map((s) => String(s._id || s.id))));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [ids]);

  // Unique display label per signpost (avoids recharts dataKey collisions).
  const labels = useMemo(() => {
    const seen = {};
    const map = {};
    quant.forEach((s) => {
      const id = String(s._id || s.id);
      let name = s.name || "Signpost";
      if (seen[name]) {
        seen[name] += 1;
        name = `${name} (${seen[name]})`;
      } else {
        seen[name] = 1;
      }
      map[id] = name;
    });
    return map;
  }, [ids]);

  const inc = included || new Set(quant.map((s) => String(s._id || s.id)));

  const chartData = useMemo(() => {
    const dateMap = {};
    quant.forEach((s) => {
      const id = String(s._id || s.id);
      if (!inc.has(id)) return;
      normalise(seriesById[id] || []).forEach(({ key, ts, label, nv }) => {
        if (!key) return;
        if (!dateMap[key]) dateMap[key] = { date: label, ts };
        if (nv != null) dateMap[key][labels[id]] = nv;
      });
    });
    return Object.values(dateMap).sort((a, b) => a.ts - b.ts);
    // eslint-disable-next-line
  }, [seriesById, included, ids]);

  if (quant.length < 2) return null; // only worth showing when there are multiple signposts
  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;

  return (
    <MDBox sx={{ border: "1px solid rgba(82,0,155,0.15)", borderRadius: 1, p: 2, mb: 2 }}>
      <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
        Combined view
      </MDTypography>
      <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Each signpost is indexed to 100 at its start, so relative movements line up with the
        individual charts while different units stay comparable.
      </MDTypography>
      <MDBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
        {quant.map((s, idx) => {
          const id = String(s._id || s.id);
          return (
            <FormControlLabel
              key={id}
              control={
                <Checkbox
                  size="small"
                  checked={inc.has(id)}
                  onChange={() =>
                    setIncluded((prev) => {
                      const next = new Set(prev || quant.map((x) => String(x._id || x.id)));
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    })
                  }
                  sx={{
                    p: 0.5,
                    color: COLORS[idx % COLORS.length],
                    "&.Mui-checked": { color: COLORS[idx % COLORS.length] },
                  }}
                />
              }
              label={<MDTypography variant="caption">{labels[id]}</MDTypography>}
            />
          );
        })}
      </MDBox>
      {chartData.length === 0 ? (
        <MDTypography variant="caption" color="text.secondary">
          No data points yet for the selected signposts.
        </MDTypography>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={["auto", "auto"]}
              label={{
                value: "Indexed (start = 100)",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11 },
              }}
            />
            <RechartsTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {quant
              .filter((s) => inc.has(String(s._id || s.id)))
              .map((s) => {
                const id = String(s._id || s.id);
                const idx = quant.findIndex((x) => String(x._id || x.id) === id);
                return (
                  <Line
                    key={id}
                    type="monotone"
                    dataKey={labels[id]}
                    stroke={COLORS[idx % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </MDBox>
  );
}
